<template>
  <div v-if="isPrint">
    <span class="print-cell" :class="[textAlign ? `text-${textAlign.toLowerCase()}` : '']">{{ inputValue }}</span>
  </div>
  <div v-else class="relative">
    <vs-input
      ref="mlInput"
      class="ml-input w-full"
      :class="textAlign"
      v-model="inputValue"
      :type="type"
      :readonly="readonly"
      :disabled="disabled"
      :icon-pack="iconPack"
      :icon="icon"
      :placeholder="$t(placeholder)"
      :max="maxValue"
      :min="minValue"
      :size="`${size} ${alignClass}`"
      :danger="danger"
      :autocomplete="autocomplete"
      @keyup="(e) => $emit('keyup', e)"
      @click="(e) => $emit('click', e)"
      @focusout="(e) => $emit('focusout', e)"
      @focus="handleFocus"
      @blur="handleBlur"
      @paste="handlePaste"
    />
    <div
      v-if="!readonly && !disabled && visibleCounter && isInputSelected"
      :class="['vs-textarea--count', 'counter', { negative: isExceeded, shake: isExceeded /* 글자 수 에러 표시 */ }]"
      style="bottom: 1px; padding: 0 6px 1px; line-height: 1rem"
    >
      {{ counterLength }} / {{ max }}
    </div>
  </div>
</template>

<script>
export default {
  name: "MaxLengthInput",
  props: {
    defaultValue: { type: String | Number, default: "" },
    type: { type: String, default: "text" },
    value: { type: String | Number, default: "" },
    placeholder: String,
    readonly: Boolean,
    disabled: Boolean,
    max: { type: Number, default: 200 },
    iconPack: String,
    icon: String,
    noneCount: String,
    minValue: Number,
    maxValue: Number,
    // input 하단에 현재 카운팅현황(0/max) 표기를 보이게 합니다.
    visibleCounter: { type: Boolean, default: false },
    size: String,
    // textAlign: left, center, right
    textAlign: {
      type: String,
      default: "left",
    },
    // 인쇄 모드일 경우, true 전달
    isPrint: {
      type: Boolean,
      default: false,
    },
    danger: Boolean,
    // 제거하는 문자들
    removeChars: { type: Array, default: () => [] },
    autocomplete: {
      type: String,
      default: "on",
    }
  },
  data() {
    return {
      inputValue: this.value || this.defaultValue,
      isExceeded: false,
      isInputSelected: false,
    };
  },
  computed: {
    alignClass() {
      return this.textAlign ? `text-${this.textAlign.toLowerCase()}` : "";
    },
    counterLength() {
      return this.inputValue.replaceAll(this.noneCount, "").length;
    },
  },
  watch: {
    value: {
      handler(value) {
        if (this.type === "number") {
          value = Number(value);
          // minValue가 설정되어 있다면, value가 minValue보다 작으면 minValue로 설정합니다.
          if (this.minValue || this.minValue === 0) {
            value = Math.max(value, this.minValue);
          }
          // maxValue가 설정되어 있다면, value가 maxValue보다 크면 maxValue로 설정합니다.
          if (this.maxValue || this.maxValue === 0) {
            value = Math.min(value, this.maxValue);
          }
        }

        this.inputValue = value.toString();
      },
    },
    inputValue: {
      immediate: true,
      deep: true,
      handler(value) {
        if (this.type === "number") {
          value = Number(value);
          // minValue가 설정되어 있다면, value가 minValue보다 작으면 minValue로 설정합니다.
          if (this.minValue || this.minValue === 0) {
            value = Math.max(value, this.minValue);
          }
          // maxValue가 설정되어 있다면, value가 maxValue보다 크면 maxValue로 설정합니다.
          if (this.maxValue || this.maxValue === 0) {
            value = Math.min(value, this.maxValue);
          }
        }

        // value에서 noneCount 문자를 모두 제거
        if (this.noneCount) value = value.replaceAll(this.noneCount, "");

        /**
         * 최대 글자 수 검증 적용
         * max 값이 설정되어 있고, 현재 입력된 글자 수가 max 길이 만큼을 초과하면,
         * 초과하기 전의 값을 유지하고, 초과했음을 알리는 isExceeded를 true로 설정합니다.
         * isExceeded 값이 true로 변하면, 카운터의 색상이 변경되고, 진동 애니메이션이 발생합니다.
         */
        if (this.max && value.length > this.max) {
          this.inputValue = this.value;
          this.isExceeded = true;

          setTimeout(() => {
            this.isExceeded = false;
          }, 300);
        } else {
          if (0 < this.removeChars.length) {
            const setValue = value
              .split("")
              .filter((char) => !this.removeChars.includes(char))
              .join("");

            this.$emit("input", setValue);
            this.$emit("change", setValue);
          } else {
            this.$emit("input", value);
            this.$emit("change", value);
          }
        }
      },
    },
  },
  methods: {
    handleFocus() {
      this.isInputSelected = true;
    },
    handleBlur() {
      this.isInputSelected = false;
    },
    handlePaste (value) {
      this.$emit("paste", value);
    }
  },
};
</script>

<style lang="scss" scoped>
.ml-input::v-deep {
  .input-span-placeholder {
    line-height: 1.2rem;
  }

  &.left input {
    text-align: left;
  }
  &.center input {
    text-align: center;
  }
  &.right input {
    text-align: right;
  }
}
.isFocus + .counter {
  opacity: 1;
}
.counter {
  transition: background-color 0.3s;
}
.counter.negative {
  background-color: #f44336;
}
@keyframes shake {
  0% {
    transform: translate(0, 100%);
  }
  10% {
    transform: translate(-3px, 100%);
  }
  20% {
    transform: translate(3px, 100%);
  }
  30% {
    transform: translate(-3px, 100%);
  }
  40% {
    transform: translate(3px, 100%);
  }
  50% {
    transform: translate(-3px, 100%);
  }
  60% {
    transform: translate(3px, 100%);
  }
  70% {
    transform: translate(-3px, 100%);
  }
  80% {
    transform: translate(3px, 100%);
  }
  90% {
    transform: translate(-3px, 100%);
  }
  100% {
    transform: translate(0, 100%);
  }
}

.counter.shake {
  animation: shake 0.3s;
}

.print-cell {
  display: block;
  width: 100%;
  background-color: white;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  padding: 7px 9px;
}
</style>

<!-- placeholder 여백이 달라져 상단에 붙는 문제가 있어서 전체 style 로 추가 -->
<style>
.ml-input > .vs-con-input > .input-span-placeholder {
  padding: 0.7rem 1rem !important;
}

.ml-input > .vs-con-input > .vs-inputx[readonly] {
  background-color: #fafafa;
}
</style>
